import { CustomFormsFeatures, cachedInflightRequest } from '@nova/core';
import { getResponse } from '@satellite/services/serviceUtilities';
import { cacheableGet } from '@satellite/services/httpClient';

export default {
  /**
   *
   * @param params
   * @param options { fields: string[], joins: string[], includeMetaData: boolean }
   * @param requestConfig
   * @param httpClient - Optional axios instance (e.g. from useHttpClient()). When not provided, uses global axios.
   * @returns {Promise<Object|*>}
   */
  async getCustomFormData(params, options = {}, requestConfig = {}, httpClient = null) {
    const client = httpClient ?? axios;
    try {
      // Generate a unique cache key
      const cacheKey = `custom-forms/form-data:${JSON.stringify(params)}`;

      return cachedInflightRequest(cacheKey, async () => {
        const response = await client.get('custom-forms/form-data', {
          params,
          ...requestConfig
        });
        return getResponse(response, options) || [];
      });
    } catch (error) {
      console.error('Error fetching custom form data:', error);
      return [];
    }
  },
  /**
   * Get loadtype triggers
   * @param loadTypeId
   * @param params
   * @param options
   * @param requestConfig
   * Returns Full join structure eagerly loaded
   * [ {...trigger: { flow: { fromFrom: { formFields: [{ field: customField }] } } } ]
   * */
  async getLoadTypeTriggers(loadTypeId, searchParams, options = {}, requestConfig = {}) {
    try {
      // Generate a unique cache key
      const cacheKey = `custom-forms/loadtype-triggers:${loadTypeId}:${JSON.stringify(
        searchParams
      )}`;
      return cachedInflightRequest(cacheKey, async () => {
        const response = await axios.get('custom-forms/trigger', {
          params: {
            s: {
              objectId: loadTypeId,
              entityName: 'loadtype',
              flowId: { $notnull: true },
              ...searchParams
            },
            limit: 1000
          },
          requestConfig
        });
        return getResponse(response, options) || [];
      });
    } catch (error) {
      console.error('Error fetching loadtype triggers:', error);
      return [];
    }
  },
  /**
   * Get warehouse triggers
   * @param warehouseId
   * @param params
   * @param options
   * @param requestConfig
   * Returns Full join structure eagerly loaded
   * [ {...trigger: { flow: { fromFrom: { formFields: [{ field: customField }] } } } ]
   * */
  async getWarehouseTriggers(warehouseId, searchParams, options = {}, requestConfig = {}) {
    try {
      // Generate a unique cache key
      const cacheKey = `custom-forms/warehouse-triggers:${warehouseId}:${JSON.stringify(
        searchParams
      )}`;
      return cachedInflightRequest(cacheKey, async () => {
        const response = await axios.get('custom-forms/trigger', {
          params: {
            s: {
              objectId: warehouseId,
              entityName: 'warehouse',
              flowId: { $notnull: true },
              feature: { $ne: CustomFormsFeatures.CheckIn },
              ...searchParams
            },
            limit: 1000
          },
          requestConfig
        });
        return getResponse(response, options) || [];
      });
    } catch (error) {
      console.error('Error fetching warehouse triggers:', error);
      return [];
    }
  },
  /**
   * Get loadtype triggers
   * @param loadTypeId
   * @param params
   * @param options
   * @param requestConfig
   * Returns Full join structure eagerly loaded
   * [ {...trigger: { flow: { fromFrom: { formFields: [{ field: customField }] } } } ]
   * */
  async getLoadTypesTriggers(loadTypeIds, searchParams, options = {}, requestConfig = {}) {
    try {
      // Ensure loadTypeIds is an array
      const idsArray = Array.isArray(loadTypeIds) ? loadTypeIds : [loadTypeIds];

      // Generate a unique cache key with sorted array for consistency
      // Use string comparison since loadTypeIds are UUIDs (strings), not numbers
      const sortedIds = [...idsArray].sort((a, b) => String(a).localeCompare(String(b)));
      const cacheKey = `custom-forms/loadtypes-triggers:${JSON.stringify(
        sortedIds
      )}:${JSON.stringify(searchParams)}`;

      return cachedInflightRequest(cacheKey, async () => {
        // NestJSX CRUD format: objectId should be in the s object with $in for arrays
        const response = await axios.get('custom-forms/trigger', {
          params: {
            s: {
              objectId: { $in: idsArray },
              entityName: 'loadtype',
              flowId: { $notnull: true },
              ...searchParams
            },
            limit: 1000
          },
          requestConfig
        });
        return getResponse(response, options) || [];
      });
    } catch (error) {
      console.error('Error fetching loadtypes triggers:', error);
      return [];
    }
  },
  /**
   * Get asset containers triggers
   * @param warehouseId
   * @param params
   * @param options
   * @param requestConfig
   * Returns Full join structure eagerly loaded
   * [ {...trigger: { flow: { fromFrom: { formFields: [{ field: customField }] } } } ]
   * */
  async getAssetContainerTriggers(warehouseId, searchParams, options = {}, requestConfig = {}) {
    try {
      // Generate a unique cache key
      const cacheKey = `custom-forms/asset-container-triggers:${warehouseId}:${JSON.stringify(
        searchParams
      )}`;
      return cachedInflightRequest(cacheKey, async () => {
        const response = await axios.get('custom-forms/trigger', {
          params: {
            s: {
              objectId: warehouseId,
              feature: CustomFormsFeatures.AssetContainerDetails,
              flowId: { $notnull: true },
              ...searchParams
            },
            limit: 1000
          },
          requestConfig
        });
        return getResponse(response, options) || [];
      });
    } catch (error) {
      console.error('Error fetching asset container triggers:', error);
      return [];
    }
  },
  /**
   * @template TRequest
   * @template TResponse
   *
   * Get asset container detail triggers (warehouse-scoped) via cacheable GET.
   * Same filter semantics as {@link getAssetContainerTriggers}; uses {@link cacheableGet} for inflight dedupe.
   *
   * @param {Object} [options]
   * @param {string} options.warehouseId - Warehouse id used as `objectId` in the `s` filter
   * @param {Record<string, unknown>} [options.searchParams] - Extra `s` filter fields (e.g. `{ isActive: true }`)
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig]
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig]
   * @param {boolean} [options.enableCache]
   * @returns {Promise<TResponse>}
   */
  async getAssetContainerTriggersV2({
    warehouseId,
    searchParams = {},
    getResponseConfig = {},
    requestConfig = {},
    enableCache = true
  } = {}) {
    if (!warehouseId) {
      return [];
    }

    try {
      const axiosResponse = await cacheableGet('custom-forms/trigger', {
        ...requestConfig,
        params: {
          s: {
            objectId: warehouseId,
            feature: CustomFormsFeatures.AssetContainerDetails,
            flowId: { $notnull: true },
            ...searchParams
          },
          limit: 1000
        },
        enableCache
      });

      return getResponse(axiosResponse, getResponseConfig) || [];
    } catch (error) {
      console.error('Error fetching asset container triggers (v2):', error);
      return [];
    }
  }
};
